import type { AccessControlProvider } from '@refinedev/core';
import type { Action, AppAbility, Subject } from './superadmin';

/** Refine resource name → CASL subject. Resources not listed are not gated
 *  (the provider allows them; route guards remain the real enforcement). */
const RESOURCE_SUBJECT: Record<string, Subject> = {
  companies: 'Company',
  conferences: 'Conference',
  users: 'User',
  toolkits: 'Toolkit',
  'prompt-templates': 'PromptTemplate',
  'core-prompts': 'CorePrompt',
  'prompt-rollout': 'CorePrompt',
  integrations: 'Integration',
  questions: 'DiscoveryQuestion',
};

/** Refine actions that mutate → require `manage`; reads (list/show) → `read`. */
const WRITE_ACTIONS = new Set(['create', 'edit', 'delete', 'clone']);

/**
 * Central Refine access-control gate for the super-admin console. Hides
 * built-in action buttons (CreateButton/EditButton/DeleteButton/…) the signed-in
 * role can't use — so a super_admin_readonly user never sees a create button that
 * would only dead-end at the route-level Access Denied page.
 *
 * `getAbility` is called fresh per check so the answer always reflects the
 * current role (App wires it to the live ability; tests inject one).
 */
export function buildAccessControlProvider(
  getAbility: () => AppAbility,
): AccessControlProvider {
  return {
    can: async ({ resource, action }) => {
      const subject = resource ? RESOURCE_SUBJECT[resource] : undefined;
      if (!subject) return { can: true }; // unmapped resource → not gated here
      const abilityAction: Action = WRITE_ACTIONS.has(action) ? 'manage' : 'read';
      return { can: getAbility().can(abilityAction, subject) };
    },
    options: { buttons: { enableAccessControl: true, hideIfUnauthorized: true } },
  };
}
